import React, { useContext, useEffect, useRef, useState } from 'react';
import { ContentTrackerProps } from './types';
import { FullScreenTrackingContext } from './FullScreenTrackingContext';
import { WindowFocusContext } from './WindowFocusContext';
import DocTracker from '../../../Services/Analytics/DocTracker';
import DocumentViewerFacade, {
  DocumentViewerFacadeProps,
} from '../../DocumentViewer/DocumentViewerFacade';
import useIntersectionObserver from '../../../lib/hook/useIntersectionObserver';

/**
 * - Using a lower intersection observer visibility threshold because
 *   there could be large documents or pages that overflow the viewport
 * - This value should higher than the PDFLoader threshold to trigger
 *   the document loading before starting to track
 */
const DOCUMENT_VISIBILITY_THRESHOLD = 0.3;

interface Props extends DocumentViewerFacadeProps, ContentTrackerProps {}

const DocumentTracker: React.FC<Props> = (props: Props): JSX.Element => {
  const { fullScreenContentId } = useContext(FullScreenTrackingContext);
  const { isWindowFocused } = useContext(WindowFocusContext);

  const {
    contactId,
    contentId,
    onLoad: onLoadProp,
    roomId,
    track,
    version,
    ...documentViewerProps
  } = props;

  const [loaded, setLoaded] = useState(false);
  const [withinViewport, setWithinViewport] = useState(false);

  const { current: tracker } = useRef(
    new DocTracker(contentId, contactId, roomId, version)
  );

  const documentRef = useIntersectionObserver<HTMLDivElement>(
    (isIntersecting: boolean) => setWithinViewport(isIntersecting),
    DOCUMENT_VISIBILITY_THRESHOLD
  );

  useEffect(
    () => () => tracker.detach(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    /**
     * Stopping the document tracking when there is something else in full screen
     * (like an image), since documents are tracked only when they are within
     * the viewport. Since there's something else blocking the view of the document,
     * we stop the tracking until the user exits the fullscreen view.
     */
    const isAnyContentInFullScreen = !!fullScreenContentId;
    const isCurrentlyInFullScreen = fullScreenContentId === contentId;

    if (
      !(
        track &&
        loaded &&
        withinViewport &&
        isWindowFocused &&
        (!isAnyContentInFullScreen || isCurrentlyInFullScreen)
      )
    ) {
      tracker.setActive(false);
      return;
    }

    /**
     * Using a set timeout just to give the documents some time to set
     * their appropriate height, which could push them outside the viewport.
     * The returned cleanup function makes sure that if the doc leaves the
     * viewport once its loaded, it won't be tracked.
     */
    const timeout = setTimeout(() => {
      tracker.setActive(!!track && loaded && withinViewport && isWindowFocused);
    }, 250);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScreenContentId, isWindowFocused, loaded, withinViewport]);

  const onDocumentViewerLoad = (pages: number) => {
    setLoaded(true);

    if (track) {
      tracker.attach(1);
    }

    if (onLoadProp) {
      onLoadProp(pages);
    }
  };

  const onPageChange = (newPage: number) => {
    /**
     * This method is called for page #1 when the document is loaded,
     * so we make sure the document is within the viewport to track the
     * page "change", to avoid unnecessary tracking for docs that are not visible
     *
     * @warning we don't detect window focus on page changes because it creates
     *          a race condition (particularly reproducible for pspdfkit viewer)
     *          and we can safely assume the window is focused
     */
    if (track && withinViewport) {
      tracker.trackProgress(newPage);
    }
  };

  return (
    <DocumentViewerFacade
      {...documentViewerProps}
      contentId={contentId}
      onLoad={onDocumentViewerLoad}
      onPageChange={onPageChange}
      ref={documentRef}
      rounded
      userContactId={contactId}
    />
  );
};

export default DocumentTracker;
